import { useState } from 'react'
import { useI18n } from 'gunsi-auth'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import { applyTheme, loadThemePrefs, saveThemePrefs } from '../theme.js'
import { Select } from '../components/Select.jsx'
import { useConfirm } from '../components/ConfirmDialog.jsx'

const CURRENCIES = ['EUR','USD','GBP','CHF','JPY','CAD','AUD','SEK','NOK','DKK']

export function Settings() {
  const t = useEvenI18n()
  const { locale, setLocale } = useI18n()
  const lang = (locale || 'en').slice(0, 2)
  const { currency, setCurrency, dateFormat, setDateFormat, importJson, resetAll, ...rest } = useEven()
  const { confirm, notify } = useConfirm()
  const [prefs, setPrefs] = useState(loadThemePrefs())

  function updateTheme(theme, colorSet) {
    saveThemePrefs(theme, colorSet)
    applyTheme(theme, colorSet)
    setPrefs({ theme, colorSet })
  }

  function doExport() {
    const data = {
      currency: rest.currency,
      groups: rest.groups,
      expenses: rest.expenses,
      settlements: rest.settlements,
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `gunsi-even-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  function doImport(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const r = new FileReader()
    r.onload = () => {
      try {
        const parsed = JSON.parse(String(r.result))
        importJson(parsed)
      } catch {
        notify({ message: t.settings.invalidJson })
      }
    }
    r.readAsText(file)
    e.target.value = ''
  }

  async function doReset() {
    const ok = await confirm({
      message: t.settings.resetConfirm,
      okLabel: t.settings.reset,
      danger: true,
    })
    if (!ok) return
    await resetAll()
  }

  return (
    <main className="page page-settings">
      <header className="page-head">
        <h1>{t.settings.title}</h1>
      </header>

      <section className="settings-section">
        <h2>{t.settings.appearance}</h2>
        <div className="settings-row">
          <label>{t.settings.theme}</label>
          <div className="seg-control">
            {['system','light','dark'].map(th => (
              <button
                key={th}
                className={prefs.theme === th ? 'active' : ''}
                onClick={() => updateTheme(th, prefs.colorSet)}
              >{t.settings[`theme${th[0].toUpperCase()}${th.slice(1)}`]}</button>
            ))}
          </div>
        </div>
        <div className="settings-row">
          <label>{t.settings.style}</label>
          <div className="seg-control">
            {['modern','classic'].map(cs => (
              <button
                key={cs}
                className={prefs.colorSet === cs ? 'active' : ''}
                onClick={() => updateTheme(prefs.theme, cs)}
              >{t.settings[`style${cs[0].toUpperCase()}${cs.slice(1)}`]}</button>
            ))}
          </div>
        </div>
        <div className="settings-row">
          <label>{t.settings.language}</label>
          <div className="settings-control">
            <Select
              value={lang}
              onChange={setLocale}
              options={[
                { value: 'en', label: 'English' },
                { value: 'it', label: 'Italiano' },
              ]}
              label={t.settings.language}
            />
          </div>
        </div>
      </section>

      <section className="settings-section">
        <h2>{t.settings.defaultCurrency}</h2>
        <div className="settings-row">
          <div className="settings-control">
            <Select
              value={currency}
              onChange={setCurrency}
              options={CURRENCIES.map(c => ({ value: c, label: c }))}
              label={t.settings.defaultCurrency}
            />
          </div>
        </div>
      </section>

      <section className="settings-section">
        <h2>{t.settings.dateFormat}</h2>
        <div className="settings-row">
          <div className="settings-control">
            <Select
              value={dateFormat ?? 'auto'}
              onChange={setDateFormat}
              options={['auto', 'dmy', 'mdy', 'ymd'].map(v => ({ value: v, label: t.settings.dateFormats[v] }))}
              label={t.settings.dateFormat}
            />
          </div>
        </div>
      </section>

      <section className="settings-section">
        <h2>{t.settings.data}</h2>
        <div className="settings-row">
          <button className="btn btn-secondary" onClick={doExport}>{t.settings.exportJson}</button>
        </div>
        <div className="settings-row">
          <label className="btn btn-secondary">
            {t.settings.importJson}
            <input type="file" accept="application/json" onChange={doImport} style={{ display: 'none' }} />
          </label>
        </div>
        <div className="settings-row">
          <button className="btn btn-danger" onClick={doReset}>{t.settings.reset}</button>
        </div>
      </section>
    </main>
  )
}
